import { gql, useQuery } from '@apollo/client';
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import { Alert, Button } from '@apollo/orbit';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link, Redirect, Route, Switch } from 'react-router-dom';

import * as orgRouteConfig from 'src/app/account/routes';
import { GlobalGraphHeader } from 'src/app/appLayout/components/globalHeader/globalGraphHeader/GlobalGraphHeader';
import { GlobalHeader } from 'src/app/appLayout/components/globalHeader/GlobalHeader';
import { CoordinateNotFoundPage } from 'src/app/graph/insights/fieldPage/CoordinateNotFoundPage';
import IconAstronaut2 from 'src/assets/icon-astronaut-2.svg';
import IconLinter from 'src/assets/icon-linter.svg';
import IconObservatory from 'src/assets/icon-observatory.svg';
import { GraphNav } from 'src/components/appNav/graphNav/GraphNav';
import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { SuperAdminWarningBanner } from 'src/components/common/superAdminWarningBanner/SuperAdminWarningBanner';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { EmptyState } from 'src/components/emptyState/EmptyState';
import { NewFedVersionToast } from 'src/components/newFedVersionToast/NewFedVersionToast';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { Toasts, useShowToast } from 'src/components/toast/Toast';
import { NewFedVersionToastContainer } from 'src/hooks/NewFedVersionToastContainer';
import { useAppSettings } from 'src/hooks/useAppSettings';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { GraphType, UserPermission } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';

import { ThemeProvider } from '../../../components/themeProvider/ThemeProvider';
import { ChangeLogPage } from '../changeLogPage/ChangeLogPage';
import { CheckLayout } from '../check/CheckLayout';
import { ChecksPage } from '../checksPage/ChecksPage';
import { ClientsPage } from '../clientsPage/ClientsPage';
import { CloudSettingsLayout } from '../cloudSettingsPage/CloudSettingsLayout';
import { STUDIO_UI_EXPLORER_FLAGS } from '../explorerPage/explorerBehavioralFlags';
import { VariantPersistedQueriesLayout } from '../graphLayout/persistedQueriesLayout/VariantPersistedQueriesLayout';
import { VariantProposalListPage } from '../graphLayout/proposalListPage/VariantProposalsListPage';
import { GraphSettingsLayout } from '../graphSettingsPage/GraphSettingsLayout';
import { useGraphVisibility } from '../hooks/useGraphVisibility';
import { useIsPublicMemberOnly } from '../hooks/useIsPublicMemberOnly';
import { FieldPage } from '../insights/fieldPage/FieldPage';
import { InsightsPage } from '../insights/InsightsPage';
import { LaunchesPage } from '../launchesPage/LaunchesPage';
import {
  changelogRouteConfig,
  checkRouteConfig,
  clientsRouteConfig,
  cloudSettingsRouteConfig,
  coordinateNotFoundRouteConfig,
  explorerRouteConfig,
  fieldRouteConfig,
  graphSettingsRouteConfig,
  insightsRouteConfig,
  launchesRouteConfig,
  recentChecksRouteConfig,
  schemaRouteConfig,
  subgraphsRouteConfig,
  variantHomepageRouteConfig,
  variantPersistedQueriesRouteConfig,
  variantProposalListRouteConfig,
  variantRouteConfig,
  variantSettingsRouteConfig,
} from '../routes';
import {
  ContinueOnboardingWidget,
  ContinueOnboardingWidgetFragment,
} from '../shared/ContinueOnboardingWidget';
import { SubgraphsLayout } from '../subgraphsLayout/SubgraphsLayout';
import { VariantSettingsLayout } from '../variantSettingsPage/VariantSettingsLayout';
import { VariantsHomepage } from '../variantsHomepage/VariantsHomepage';

import { STUDIO_UI_GRAPH_FLAGS } from './graphBehavioralFlags/graphBehavioralFlags';
import { NonPublicGraphArea } from './NonPublicGraphArea';
import { ProtectedGraphArea } from './ProtectedGraphArea';
import { VariantLayoutModals } from './VariantLayoutModals';
import { VariantLayoutProviders } from './VariantLayoutProviders';
const ExplorerPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ExplorerPage" */ '../explorerPage/ExplorerPage'
    )
  ).ExplorerPage,
}));

const DocumentationPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "DocumentationPage" */ '../documentationPage/DocumentationPage'
    )
  ).DocumentationPage,
}));

const VARIANT_LAYOUT_USER_QUERY = gql<
  GraphQLTypes.VariantLayoutUserQuery,
  GraphQLTypes.VariantLayoutUserQueryVariables
>`
  query VariantLayoutUserQuery {
    me {
      id
      ... on User {
        internalAdminRole
        memberships {
          role
          account {
            id
          }
          user {
            id
          }
        }
      }
    }
  }
`;

const VARIANT_LAYOUT_GRAPH_QUERY = gql`
  query VariantLayoutGraphQuery($graphId: ID!, $graphVariant: String!) {
    service(id: $graphId) {
      id
      title
      graphType
      variant(name: $graphVariant) {
        id
        isPublic
        hasManagedSubgraphs
        isContract
        url
        isProposal
        latestLaunch {
          id
          build {
            id
            result {
              ... on BuildFailure {
                errorMessages {
                  code
                  locations {
                    column
                    line
                  }
                  message
                }
              }
            }
          }
        }
      }
      account {
        id
      }
      ...ContinueOnboardingWidgetFragment
    }
  }
  ${ContinueOnboardingWidgetFragment}
`;

export const VariantLayout = () => {
  const history = useHistory();
  const location = useLocation();
  const [currentAccountId, setCurrentAccountId] = useCurrentAccountId();
  const graphVisibilityType = useGraphVisibility();
  const { graphId, graphVariant } = useRouteParams(variantRouteConfig);
  const graphRef = useMemo(
    () => ({ graphId, graphVariant }),
    [graphId, graphVariant],
  );
  const isPublicMemberOnly = useIsPublicMemberOnly({ graphRef });
  const canViewNewPQUI = useLDFlag('gov-pq-ux-sort-filter-delete');

  // TODO: This should be different initially depending on the route. For
  // example, landing on studio cold should set `collapsed` to true.
  const { navCollapsed, setNavCollapsed } = useAppSettings();

  // TODO: Can we use Orbit toasts for these?
  const { hideToasts } = useShowToast();
  const stableToastId = React.useMemo(() => uniqueId(), []);

  React.useEffect(() => {
    // cleanup toast on unmount
    return () => hideToasts(stableToastId);
  }, [hideToasts, stableToastId]);

  const { data: userData, loading: userLoading } = useQuery(
    VARIANT_LAYOUT_USER_QUERY,
    { context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }) },
  );

  const { data: graphData, loading: graphLoading } = useQuery<
    GraphQLTypes.VariantLayoutGraphQuery,
    GraphQLTypes.VariantLayoutGraphQueryVariables
  >(VARIANT_LAYOUT_GRAPH_QUERY, {
    variables: {
      graphId,
      graphVariant,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    fetchPolicy: 'cache-and-network',
  });

  const me = userData?.me;
  const isBillingManager =
    me?.__typename === 'User' &&
    me?.memberships?.find(
      (m) =>
        m.account.id === currentAccountId &&
        m.role === UserPermission.BILLING_MANAGER,
    );
  const graph = graphData?.service;
  const variant = graph?.variant;
  const accountId = graph?.account?.id ?? currentAccountId ?? null;
  const isCloudSupergraph = graph?.graphType === GraphType.CLOUD_SUPERGRAPH;
  const graphIsManaged =
    Boolean(variant?.hasManagedSubgraphs) || Boolean(variant?.isContract);
  const isProposal = Boolean(variant?.isProposal);
  const latestBuild = variant?.latestLaunch?.build;
  const buildErrors =
    latestBuild?.result?.__typename === 'BuildFailure'
      ? latestBuild.result.errorMessages
      : [];

  // Preload permissions so we can consistently show blocks instead of flashing no permission errors
  const { loading: loadingPermissions } = usePermissions(
    accountId ?? undefined,
    graphId,
    graphVariant,
  );

  // Only set this when accountId is truthy to avoid an infinite render
  // cycle where this nulls out `currentAccountId` and then `useIdentity`
  // sets it back to the users default org.
  React.useEffect(() => {
    if (accountId && accountId !== currentAccountId) {
      setCurrentAccountId(accountId);
    }
  }, [setCurrentAccountId, accountId, currentAccountId]);

  if (graphLoading || userLoading || loadingPermissions) {
    return <Loading />;
  }

  if (
    isPublicMemberOnly &&
    graphVisibilityType !== 'public' &&
    variant?.isPublic
  ) {
    return (
      <Redirect
        push={false}
        to={variantRouteConfig.locationFrom({
          location,
          patch: {
            graphVisibilityType: 'public',
          },
        })}
      />
    );
  }

  if (
    isBillingManager ||
    // Only let public members view via the public route
    (isPublicMemberOnly && graphVisibilityType !== 'public')
  ) {
    return (
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <GlobalHeader
          leftNavContext={navCollapsed ? 'collapsed' : 'expanded'}
        />
        <div className="flex h-full flex-1 overflow-y-auto">
          <NotFound
            image="satellite"
            title="Oops, no sign of life here..."
            subtitle={
              <>
                <div className="mb-8">
                  The graph you are looking for could not be found. Please go
                  back to the <b>Graph List</b> and select another graph.
                </div>
                <Button leftIcon={<IconArrowLeft />} as={Link} to="/">
                  Graph List
                </Button>
              </>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <VariantLayoutProviders
      graphRef={graphRef}
      graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
    >
      <ErrorBoundary
        renderError={() => (
          <Alert status="warning">
            Error getting federation version information.
          </Alert>
        )}
      >
        <NewFedVersionToastContainer
          toastId={stableToastId}
          render={({ onClose }) => (
            <NewFedVersionToast
              onClick={() => {
                history.push(
                  variantSettingsRouteConfig.path({
                    graphId,
                    graphVariant,
                    tab: 'general',
                  }),
                );
                onClose();
              }}
            />
          )}
        />
      </ErrorBoundary>
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
        <GlobalHeader
          leftNavContext={navCollapsed ? 'collapsed' : 'expanded'}
          goHomeLink={
            graphData?.service?.account?.id
              ? orgRouteConfig.org.path({ orgId: graphData.service.account.id })
              : undefined
          }
        >
          <GlobalGraphHeader
            key={graphId}
            graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
          />
        </GlobalHeader>

        <div className="flex h-full flex-1 overflow-y-auto">
          <Toasts />
          <GraphNav collapsed={navCollapsed} setCollapsed={setNavCollapsed} />
          <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            {me?.__typename === 'User' &&
              !me.memberships.some((m) => m.account.id === accountId) &&
              me.internalAdminRole &&
              accountId && (
                <SuperAdminWarningBanner
                  internalAdminRole={me.internalAdminRole}
                  accountId={accountId}
                />
              )}
            <DocumentTitle title={`${graph?.title ?? graphId}@${graphVariant}`}>
              <ErrorBoundary>
                {/* If route is for the wrong type, render not found */}
                {isProposal ? (
                  <NotFound />
                ) : (
                  <Switch>
                    <Route
                      path={variantHomepageRouteConfig.definition}
                      render={() => (
                        <ProtectedGraphArea accountId={accountId}>
                          <DocumentTitle title="Overview" />
                          <VariantsHomepage
                            isCloudSupergraph={isCloudSupergraph}
                          />
                        </ProtectedGraphArea>
                      )}
                    />
                    {isCloudSupergraph && (
                      <Route
                        path={cloudSettingsRouteConfig.definition}
                        render={() => (
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Cloud Router" />
                            <CloudSettingsLayout />
                          </ProtectedGraphArea>
                        )}
                      />
                    )}
                    <Route
                      path={schemaRouteConfig.definition}
                      render={() => (
                        <ProtectedGraphArea accountId={accountId}>
                          <DocumentTitle title="Schema" />
                          <DocumentationPage />
                        </ProtectedGraphArea>
                      )}
                    />

                    <Route
                      path={fieldRouteConfig.definition}
                      render={() => {
                        return (
                          <NonPublicGraphArea>
                            <ProtectedGraphArea accountId={accountId}>
                              <PermissionGuard
                                graphId={graphId}
                                graphPermissions="canQueryStats"
                                fallback={PermissionBlockCard}
                              >
                                <FieldPage />
                              </PermissionGuard>
                            </ProtectedGraphArea>
                          </NonPublicGraphArea>
                        );
                      }}
                    />
                    <Route
                      path={coordinateNotFoundRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Fields" />
                            <PermissionGuard
                              graphId={graphId}
                              graphPermissions="canQueryStats"
                              fallback={PermissionBlockCard}
                            >
                              <CoordinateNotFoundPage />
                            </PermissionGuard>
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={variantPersistedQueriesRouteConfig.definition}
                      render={() => {
                        if (!canViewNewPQUI) {
                          return <NotFound />;
                        }

                        return (
                          <ProtectedGraphArea
                            accountId={accountId}
                            requiresPlanCapability="persistedQueries"
                          >
                            <DocumentTitle title="Persisted Queries">
                              <VariantPersistedQueriesLayout />
                            </DocumentTitle>
                          </ProtectedGraphArea>
                        );
                      }}
                    />
                    {/* Checks Page Route */}
                    <Route
                      path={recentChecksRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Checks">
                              <PermissionGuard
                                graphId={graphId}
                                graphPermissions={[
                                  'canCheckSchemas',
                                  'canQueryStats',
                                ]}
                                fallback={PermissionBlockCard}
                              >
                                {variant ? (
                                  <ChecksPage />
                                ) : (
                                  <div className="mt-20 flex size-full justify-center">
                                    <EmptyState
                                      icon={<IconLinter />}
                                      title="Checks"
                                      description={
                                        <>
                                          <a
                                            className="text-link underline"
                                            href="https://www.apollographql.com/docs/graphos/platform/schema-management/checks"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Checks
                                          </a>{' '}
                                          automatically validate your schema so
                                          you can catch potential issues early,
                                          ensuring your graph stays stable and
                                          client-safe as it evolves.
                                        </>
                                      }
                                    >
                                      <ContinueOnboardingWidget />
                                    </EmptyState>
                                  </div>
                                )}
                              </PermissionGuard>
                            </DocumentTitle>
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={checkRouteConfig.definition}
                      component={CheckLayout}
                    />
                    {graphIsManaged && [
                      /* should this also exclude contracts? */
                      <Route
                        key="launches"
                        path={launchesRouteConfig.definition}
                        render={() => (
                          <NonPublicGraphArea
                          // This is never actually for users not in the org, public users cannot fetch capabilities
                          >
                            <ProtectedGraphArea
                              accountId={accountId}
                              requiresPlanCapability="launches"
                            >
                              <DocumentTitle title="Launches">
                                <PermissionGuard
                                  graphId={graphId}
                                  graphPermissions={[
                                    'canCheckSchemas',
                                    'canQueryStats',
                                  ]}
                                  fallback={PermissionBlockCard}
                                >
                                  <LaunchesPage
                                    isCloudSupergraph={isCloudSupergraph}
                                  />
                                </PermissionGuard>
                              </DocumentTitle>
                            </ProtectedGraphArea>
                          </NonPublicGraphArea>
                        )}
                      />,
                      <Route
                        key="proposals"
                        path={variantProposalListRouteConfig.definition}
                        render={() => (
                          <NonPublicGraphArea>
                            <ProtectedGraphArea
                              accountId={accountId}
                              requiresPlanCapability="proposals"
                            >
                              <DocumentTitle title="Proposals List">
                                <PermissionGuard
                                  graphId={graphId}
                                  graphPermissions="canQueryProposals"
                                  fallback={PermissionBlockCard}
                                >
                                  <VariantProposalListPage />
                                </PermissionGuard>
                              </DocumentTitle>
                            </ProtectedGraphArea>
                          </NonPublicGraphArea>
                        )}
                      />,
                    ]}
                    <Route
                      path={clientsRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea
                            accountId={accountId}
                            requiresPlanCapability="clients"
                          >
                            <DocumentTitle title="Clients" />
                            <PermissionGuard
                              graphId={graphId}
                              graphPermissions="canQueryStats"
                              fallback={PermissionBlockCard}
                            >
                              {variant ? (
                                <ClientsPage />
                              ) : (
                                <div className="mt-20 flex size-full justify-center">
                                  <EmptyState
                                    icon={<IconAstronaut2 />}
                                    title="Clients"
                                    description="Whether it’s mobile, web, or a CLI tool, each connected client gives you a clearer picture of how your graph is powering real apps."
                                  >
                                    <ContinueOnboardingWidget />
                                  </EmptyState>
                                </div>
                              )}
                            </PermissionGuard>
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={insightsRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea
                            accountId={accountId}
                            requiresPlanCapability="metrics"
                          >
                            <DocumentTitle title="Insights" />
                            {!variant ? (
                              <div className="mt-20 flex size-full justify-center">
                                <EmptyState
                                  icon={<IconObservatory />}
                                  title="Insights"
                                  description={
                                    <>
                                      Once you’ve published a graph,{' '}
                                      <a
                                        className="text-link underline"
                                        href="https://www.apollographql.com/docs/graphos/platform/insights/sending-operation-metrics"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        enabling metrics
                                      </a>{' '}
                                      gives you access to real request data,
                                      performance bottlenecks, and operation
                                      details so you can better understand and
                                      improve your API.
                                    </>
                                  }
                                >
                                  <ContinueOnboardingWidget />
                                </EmptyState>
                              </div>
                            ) : (
                              <PermissionGuard
                                graphId={graphId}
                                graphPermissions={[
                                  'canQueryStats',
                                  'canQueryTraces',
                                ]}
                                fallback={PermissionBlockCard}
                              >
                                <InsightsPage />
                              </PermissionGuard>
                            )}
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={graphSettingsRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Graph Settings" />
                            <GraphSettingsLayout />
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    <Route
                      path={variantSettingsRouteConfig.definition}
                      render={() => (
                        <NonPublicGraphArea>
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Variant Settings" />
                            <VariantSettingsLayout
                              isCloudSupergraph={isCloudSupergraph}
                            />
                          </ProtectedGraphArea>
                        </NonPublicGraphArea>
                      )}
                    />
                    {
                      // NOTE: If a user creates a supergraph with an invalid subgraph schema
                      // the value for graphIsManaged will be false since there is no valid
                      // supergraph schema. We bypass this by checking to see if the latest
                      // build has errors due to composition failure. We need this, otherwise
                      // this tab wont show up to let users fix their graphs.
                      (graphIsManaged || isMinLengthArray(1, buildErrors)) && (
                        <Route
                          path={subgraphsRouteConfig.definition}
                          render={() => (
                            <SubgraphsLayout
                              isCloudSupergraph={isCloudSupergraph}
                            />
                          )}
                        />
                      )
                    }
                    <Route
                      path={explorerRouteConfig.definition}
                      render={() => {
                        return (
                          /**
                           * TODO(dark-mode)
                           * We need to wrap Explorer in its own ThemeProvider right now because it's
                           * the only part of the app that publicly supports Dark Mode.
                           * When we're ready to globally roll out dark mode, we will no longer need
                           * this extra wrapping because Explorer should fall back to the ThemeProvider
                           * wrapped around the entire app.
                           *
                           * Steps to roll out dark mode:
                           *     1. Update logic that holds Explorer's dark mode preference to control
                           *        the global app dark mode instead.
                           *     2. Move this control out of Explorer's page settings and into a global
                           *        settings space, likely top nav or User Settings.
                           *     2. Figure out what to do with the ThemeProviders wrapped around other
                           *        things in the app like Embedded Explorer.
                           *     3. Test LD feature flag thoroughly.
                           */
                          <ProtectedGraphArea accountId={accountId}>
                            <DocumentTitle title="Explorer" />
                            <ThemeProvider className="size-full">
                              <ExplorerPage
                                userId={me?.id}
                                explorerBehavioralFlags={
                                  STUDIO_UI_EXPLORER_FLAGS
                                }
                                graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
                              />
                            </ThemeProvider>
                          </ProtectedGraphArea>
                        );
                      }}
                    />
                    <Route
                      path={changelogRouteConfig.definition}
                      render={() => (
                        <ProtectedGraphArea accountId={accountId}>
                          <DocumentTitle title="Changelog" />
                          <ChangeLogPage />
                        </ProtectedGraphArea>
                      )}
                    />

                    <Route component={NotFound} />
                  </Switch>
                )}
              </ErrorBoundary>
            </DocumentTitle>
            <VariantLayoutModals
              currentAccountId={currentAccountId}
              graphRef={graphRef}
              graphBehavioralFlags={STUDIO_UI_GRAPH_FLAGS}
              graphTitle={graph?.title}
              variantUrl={graph?.variant?.url}
            />
          </div>
        </div>
      </div>
    </VariantLayoutProviders>
  );
};
